import { forwardRef, useCallback, useRef, useState } from "react";
import { LoaderCircle, Mic, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useChatWidget } from "@/lib/chatWidget";
import { BuiltBuddyAvatar } from "@/components/builtbuddy/BuiltBuddyAvatar";
import { BuiltBuddyGreeting, useGreeting } from "@/components/builtbuddy/BuiltBuddyGreeting";

/**
 * Fixed slot for the BuiltBuddy launcher — a bottom corner of the viewport, 32px in, above everything on the page
 * (sidebar, sticky headers, dashboard cards). Hidden while a modal dialog / mobile menu has the page scroll-locked
 * (see .bb-launcher in index.css) so it never covers a dialog's buttons. `data-corner="left"` flips it to the left.
 */
export const LAUNCHER_SLOT_CLASS = "bb-launcher fixed bottom-8 right-8 z-[9999] transition-opacity duration-150";

const CORNER_KEY = "bb_corner"; // per-device: "left" | "right"
const HOLD_MS = 500; // press-and-hold → talk
const DRAG_PX = 8; // movement before a press becomes a drag

export const readCorner = () => {
  try {
    return localStorage.getItem(CORNER_KEY) === "left" ? "left" : "right";
  } catch {
    return "right";
  }
};

/**
 * Gesture + placement for a launcher bubble:
 *  - tap → onTap · press-and-hold (500 ms) → onHold · drag → follows the finger, snaps to the nearest bottom corner
 *    on release and remembers it per device (localStorage bb_corner).
 *  - the click that follows a drag or a hold is swallowed so the panel / picker doesn't also open.
 * Motion is transform-only. Spread `slotProps` on the fixed wrapper and `buttonProps` on the button.
 */
export const useLauncherSlot = ({ onTap, onHold, offset = null } = {}) => {
  const [corner, setCorner] = useState(readCorner);
  const [drag, setDrag] = useState(null); // {dx, dy} while dragging
  const g = useRef({ start: null, moved: false, held: false, timer: null, suppressClick: false });

  const clearTimer = () => {
    if (g.current.timer) clearTimeout(g.current.timer);
    g.current.timer = null;
  };

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      g.current = { start: { x: e.clientX, y: e.clientY }, moved: false, held: false, timer: null, suppressClick: false };
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        // capture unsupported — gestures still work while the pointer stays over the button
      }
      g.current.timer = setTimeout(() => {
        if (g.current.moved || !g.current.start) return;
        g.current.held = true;
        try {
          navigator.vibrate?.(12);
        } catch {
          // no haptics — fine
        }
        onHold?.();
      }, HOLD_MS);
    },
    [onHold]
  );

  const onPointerMove = useCallback((e) => {
    const s = g.current.start;
    if (!s || g.current.held) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!g.current.moved && Math.hypot(dx, dy) < DRAG_PX) return;
    if (!g.current.moved) {
      g.current.moved = true;
      clearTimer();
    }
    setDrag({ dx, dy });
  }, []);

  const finish = useCallback((e) => {
    const r = g.current;
    clearTimer();
    if (!r.start) return;
    if (r.moved && e) {
      const next = e.clientX < window.innerWidth / 2 ? "left" : "right";
      setCorner(next);
      try {
        localStorage.setItem(CORNER_KEY, next);
      } catch {
        // storage unavailable — position still applies for this page
      }
    }
    r.suppressClick = r.moved || r.held;
    r.start = null;
    setDrag(null);
  }, []);

  const onClick = useCallback(
    (e) => {
      if (g.current.suppressClick) {
        g.current.suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onTap?.(e);
    },
    [onTap]
  );

  const style = {
    ...(offset ? { "--bb-bottom": offset.bottom, "--bb-right": offset.right, "--bb-left": offset.left ?? offset.right } : {}),
    ...(drag ? { transform: `translate(${drag.dx}px, ${drag.dy}px)` } : {}),
  };

  return {
    corner,
    dragging: !!drag,
    slotProps: { className: LAUNCHER_SLOT_CLASS, style, "data-corner": corner, "data-dragging": drag ? "true" : undefined },
    buttonProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
      onClick,
      onContextMenu: (e) => e.preventDefault(),
      style: { touchAction: "none" },
    },
  };
};

/**
 * The round BuiltBuddy button — BuiltOS navy with a steel-blue ring and the org's animated avatar (Settings › Chat widget).
 * state: idle | listening | thinking | speaking · alert: "" | "critical" | "warning" (dot colour — red only for critical/major clashes)
 * badge: unread RFI replies (green count, top-left) · pulse: animated steel ring while there is something unread.
 * avatar: override the org's avatar style (Settings preview); idleNudge: hop every few seconds while the chat is closed.
 */
export const BuiltBuddyLauncherButton = forwardRef(
  ({ state = "idle", alert = "", badge = 0, pulse = false, avatar = "", idleNudge = true, className = "", ...props }, ref) => {
    const widget = useChatWidget();
    const avatarStyle = avatar || widget.avatar_style;
    const StateIcon = state === "thinking" ? LoaderCircle : state === "listening" ? Mic : state === "speaking" ? Volume2 : null;
    return (
      <Button
        ref={ref}
        size="icon"
        aria-label={
          state === "speaking"
            ? "BuiltBuddy is reading the RFI replies — tap to stop"
            : badge
              ? `Open BuiltBuddy — ${badge} RFI repl${badge === 1 ? "y" : "ies"} waiting`
              : `Open BuiltBuddy — ${widget.greeting_name}, your personal ${widget.greeting_role}`
        }
        className={`relative h-14 w-14 select-none rounded-full border-2 border-brand-steel bg-brand-navy text-brand-pale shadow-[var(--shadow-lift)] transition-[transform,background-color,border-color] duration-150 hover:border-brand-steel hover:bg-brand-steel-deep focus-visible:ring-2 focus-visible:ring-brand-steel focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97] ${
          state === "listening" ? "ring-2 ring-[hsl(var(--info))] ring-offset-2 ring-offset-background" : state === "speaking" ? "ring-2 ring-[hsl(var(--success))] ring-offset-2 ring-offset-background" : ""
        } ${className}`}
        data-testid="builtbuddy-launcher-button"
        data-state-bb={state}
        data-badge={badge || undefined}
        data-avatar={avatarStyle}
        {...props}
      >
        {pulse ? <span className="bb-pulse pointer-events-none absolute inset-0 rounded-full" aria-hidden data-testid="builtbuddy-launcher-pulse" /> : null}
        <span className={`pointer-events-none absolute inset-0 overflow-hidden rounded-full [&_svg]:size-full ${idleNudge && state === "idle" ? "bb-av-idle" : ""}`} aria-hidden>
          <BuiltBuddyAvatar style={avatarStyle} className="h-full w-full" />
        </span>
        <span className="sr-only">BuiltBuddy</span>
        {StateIcon ? (
          <span
            className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-brand-steel text-white"
            aria-hidden
            data-testid="builtbuddy-launcher-state-chip"
          >
            <StateIcon className={`!h-3 !w-3 ${state === "thinking" ? "animate-spin" : ""}`} strokeWidth={2.4} />
          </span>
        ) : (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background bg-[hsl(var(--success))]"
            aria-hidden
            title="Online"
            data-testid="builtbuddy-launcher-online-dot"
          />
        )}
        {badge ? (
          <span
            className="absolute -left-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-[hsl(var(--success))] px-1 font-mono-nums text-[10px] font-bold leading-none text-white tabular-nums"
            data-testid="builtbuddy-launcher-badge"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
        {alert ? (
          <span
            className={`absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-background ${
              alert === "critical" ? "bg-[hsl(var(--destructive))]" : "bg-[hsl(var(--warning))]"
            }`}
            aria-hidden
            data-testid="builtbuddy-launcher-alert-dot"
          />
        ) : null}
      </Button>
    );
  }
);
BuiltBuddyLauncherButton.displayName = "BuiltBuddyLauncherButton";

/**
 * Job-scoped launcher (rendered by BuiltBuddyDock on a Job page). Tap opens the panel, hold opens it listening,
 * drag parks it in either bottom corner. Disappears while the panel is open. Shows the greeting bubble 3 s after
 * the page loads until the panel is opened once this session.
 */
export const BuiltBuddyLauncher = ({ open, onClick, onHold, state = "idle", alert = "", badge = 0 }) => {
  const slot = useLauncherSlot({ onTap: onClick, onHold });
  const widget = useChatWidget();
  const greeting = useGreeting({ open, enabled: widget.greeting_enabled });
  if (open) return null;
  return (
    <div {...slot.slotProps} data-testid="builtbuddy-launcher">
      {greeting.visible ? (
        <BuiltBuddyGreeting text={widget.greeting_text} avatar={widget.avatar_style} corner={slot.corner} onOpen={() => onClick?.()} onDismiss={greeting.dismiss} />
      ) : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <BuiltBuddyLauncherButton {...slot.buttonProps} state={state} alert={alert} badge={badge} pulse={badge > 0} />
        </TooltipTrigger>
        <TooltipContent side={slot.corner === "left" ? "right" : "left"} className="text-xs">
          {state === "speaking" ? "Reading the replies · tap to stop" : badge ? `${badge} RFI repl${badge === 1 ? "y" : "ies"} landed · hold to hear them` : "Tap to open · hold to talk"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
